import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { MRT_ColumnDef as MRTColumnDef } from 'material-react-table'
import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useAppDispatch } from '../../../app/hooks'
import { useMe } from '../../../app/hooks/useMe'
import { useNotificationStack } from '../../../app/hooks/useNotificationStack'
import {
  apiImpersonation,
  useEndAllImpersonationSessionsMutation,
  useGetImpersonationEventCountQuery,
  useGetImpersonationGrantsQuery,
  useRevokeImpersonationGrantMutation,
} from '../../../app/redux-fetch/apiImpersonation'
import AddButton from '../../../components/Buttons/AddButton'
import MRTDataGridv2 from '../../../components/MRTDataGrid/MRTDataGridv2'
import { TableTitleTypography } from '../../../components/Typographies/TableTitleTypography'
import GrantSessionsPanel from './GrantSessionsPanel'
import { buildGrantsCsv, downloadCsv } from './grantsCsv'
import ImpersonationStatusBadge from './ImpersonationStatusBadge'
import NewGrantModal from './NewGrantModal'
import { useImpersonationStart } from './useImpersonationStart'
import messages from './messages'
import {
  BULK_REVOKE_CONFIRM_PHRASE,
  GetImpersonationGrantsParams,
  IMPERSONATION_GRANT_STATUSES,
  ImpersonationGrantStatus,
  ImpersonationGrantSummary,
} from './types'

const formatDate = (iso?: string | null) => (iso ? dayjs(iso).format('YYYY-MM-DD HH:mm') : '—')

// The server composes display names with CONCAT, so a grant whose user rows have NULL name parts arrives as
// whitespace (or ''); fall back to the "User #{id}" label in that case rather than rendering a blank cell.
const nameOrId = (displayName: string | undefined, fallback: string) =>
  displayName && displayName.trim() ? displayName : fallback

// E11 R1 (WI 611) — the platform Global-Admin impersonation grants page. GA-only (useMe().isGlobalAdmin); a
// non-GA sees a Forbidden panel and NO grants API call is made (the query is skipped). MRT table with page-side
// pagination (50) and default sort GrantedAt (startsAt) DESC. Status/Tenant/Grantee filters drive the list
// query params. Per-row Revoke (Active rows only) confirms, optimistically flips to Revoked, and rolls back on
// error.
const ImpersonationGrantsPage = () => {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()
  const { enqueueSuccess, enqueueFormattedSuccess } = useNotificationStack()
  const { me, isGlobalAdmin, isSuccess: meLoaded } = useMe()
  const { start, isStarting } = useImpersonationStart()
  // The persona the caller is acting as: you can only START a grant where YOU are the grantee, and only when
  // not already impersonating (the server enforces both; this just scopes the button).
  const activePersonaId = me?.activePersona?.id
  const alreadyImpersonating = Boolean(me?.impersonation)

  // Committed filter params (applied on Apply / Clear), driving the query string.
  const [status, setStatus] = useState<ImpersonationGrantStatus | ''>('')
  const [tenantId, setTenantId] = useState<string>('')
  const [granteeId, setGranteeId] = useState<string>('')
  const [appliedParams, setAppliedParams] = useState<GetImpersonationGrantsParams>({})

  const [newOpen, setNewOpen] = useState(false)
  const [toRevoke, setToRevoke] = useState<ImpersonationGrantSummary | undefined>()
  const [toStart, setToStart] = useState<ImpersonationGrantSummary | undefined>()

  // Break-glass bulk-revoke (WI 619): the dialog is open when bulkOpen; the confirm is gated on the operator
  // typing the exact phrase (the server re-validates it too).
  const [bulkOpen, setBulkOpen] = useState(false)
  const [bulkPhrase, setBulkPhrase] = useState('')
  const [endAll, { isLoading: isEndingAll }] = useEndAllImpersonationSessionsMutation()

  // GA-only: skip the grants read entirely for a non-GA (no API call), so the Forbidden panel never fetches.
  const {
    data,
    isFetching,
    isSuccess: grantsLoaded,
  } = useGetImpersonationGrantsQuery(appliedParams, {
    skip: !isGlobalAdmin,
    refetchOnMountOrArgChange: true,
  })

  const [revokeGrant] = useRevokeImpersonationGrantMutation()

  // WI 612 — the "events in the last 30 days" badge (GA-only; skip for a non-GA so the Forbidden panel never
  // fetches). Default window (30 days) — the query sends no `days` param and the sproc defaults it.
  const { data: eventCount } = useGetImpersonationEventCountQuery(undefined, { skip: !isGlobalAdmin })

  const grants = data?.grants ?? []

  const applyFilters = () => {
    const params: GetImpersonationGrantsParams = {}
    if (status) params.status = status
    const t = Number(tenantId)
    if (tenantId.trim() !== '' && Number.isInteger(t) && t > 0) params.tenantId = t
    const g = Number(granteeId)
    if (granteeId.trim() !== '' && Number.isInteger(g) && g > 0) params.granteeId = g
    setAppliedParams(params)
  }

  const clearFilters = () => {
    setStatus('')
    setTenantId('')
    setGranteeId('')
    setAppliedParams({})
  }

  const onRevoke = async () => {
    if (!toRevoke) return
    const grant = toRevoke
    setToRevoke(undefined)
    // Optimistic flip to Revoked in the cached list; roll back on error.
    const patch = dispatch(
      apiImpersonation.util.updateQueryData('getImpersonationGrants', appliedParams, (draft) => {
        const row = draft.grants.find((x) => x.grantId === grant.grantId)
        if (row) {
          row.status = 'Revoked'
          row.revokedAt = new Date().toISOString()
        }
      })
    )
    try {
      await revokeGrant({ id: grant.grantId }).unwrap()
      enqueueSuccess(messages.revokeSuccess)
    } catch {
      // The shared base-query toast already surfaced the error; undo the optimistic flip.
      patch.undo()
    }
  }

  const onStart = () => {
    if (!toStart) return
    const grant = toStart
    setToStart(undefined)
    // Fire-and-navigate: on success useImpersonationStart hard-reloads into the impersonated context; if the
    // token lacks the c1 step-up it redirects to MFA (the pending start is persisted + resumed on return).
    start(grant.grantId)
  }

  const closeBulk = () => {
    setBulkOpen(false)
    setBulkPhrase('')
  }

  const onBulkRevoke = async () => {
    try {
      const endedCount = await endAll({ confirmPhrase: bulkPhrase }).unwrap()
      enqueueFormattedSuccess(formatMessage(messages.bulkRevokeSuccess, { count: endedCount }))
      closeBulk()
    } catch {
      // The shared base query already toasted (400 wrong phrase / 401 step-up handled by the interceptor /
      // 429 rate-limited); keep the dialog open so the operator can retry.
    }
  }

  const columns = useMemo<MRTColumnDef<ImpersonationGrantSummary>[]>(
    () => [
      {
        accessorKey: 'status',
        header: formatMessage(messages.statusColumn),
        size: 110,
        Cell: ({ row }) => <ImpersonationStatusBadge status={row.original.status} />,
      },
      {
        accessorKey: 'grantorDisplayName',
        header: formatMessage(messages.grantorColumn),
        size: 140,
        Cell: ({ row }) =>
          nameOrId(
            row.original.grantorDisplayName,
            formatMessage(messages.userIdShort, { id: row.original.grantedByUserId })
          ),
      },
      {
        accessorKey: 'granteeDisplayName',
        header: formatMessage(messages.granteeColumn),
        size: 140,
        Cell: ({ row }) =>
          nameOrId(
            row.original.granteeDisplayName,
            formatMessage(messages.userIdShort, { id: row.original.granteeUserId })
          ),
      },
      {
        accessorKey: 'targetDisplayName',
        header: formatMessage(messages.targetColumn),
        size: 160,
        // Target: name over email so a Global Admin can disambiguate two same-named users across tenants.
        Cell: ({ row }) => (
          <>
            <Typography variant='body2'>
              {nameOrId(
                row.original.targetDisplayName,
                formatMessage(messages.userIdShort, { id: row.original.targetUserId })
              )}
            </Typography>
            {row.original.targetEmail && (
              <Typography variant='caption' sx={{ color: '#636363' }}>
                {row.original.targetEmail}
              </Typography>
            )}
          </>
        ),
      },
      {
        accessorKey: 'targetTenantName',
        header: formatMessage(messages.targetTenantColumn),
        size: 130,
        Cell: ({ row }) => row.original.targetTenantName || '—',
      },
      {
        accessorKey: 'startsAt',
        header: formatMessage(messages.grantedAtColumn),
        Cell: ({ row }) => formatDate(row.original.startsAt),
      },
      {
        accessorKey: 'expiresAt',
        header: formatMessage(messages.expiresAtColumn),
        Cell: ({ row }) => formatDate(row.original.expiresAt),
      },
      {
        accessorKey: 'reason',
        header: formatMessage(messages.reasonColumn),
      },
    ],
    [formatMessage]
  )

  // Non-GA: Forbidden panel, NO grants query (skipped above). Wait for /me before deciding so a GA does not
  // flash the Forbidden panel on first render.
  if (meLoaded && !isGlobalAdmin) {
    return (
      <Paper sx={{ p: 4, m: 2 }} aria-label='impersonation-forbidden'>
        <Typography variant='h6' sx={{ mb: 1 }}>
          {formatMessage(messages.forbiddenTitle)}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {formatMessage(messages.forbiddenBody)}
        </Typography>
      </Paper>
    )
  }

  return (
    <>
      <NewGrantModal open={newOpen} onClose={() => setNewOpen(false)} />

      <Dialog open={Boolean(toRevoke)} onClose={() => setToRevoke(undefined)}>
        <DialogTitle>{formatMessage(messages.revokeConfirmTitle)}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {formatMessage(messages.revokeConfirmBody)}
          </DialogContentText>
          <Typography variant='body2' sx={{ color: '#636363' }}>
            {formatMessage(messages.revokeReasonLabel)}
          </Typography>
          {/* Plain text — React escapes it, so the stored reason is XSS-safe. */}
          <Typography variant='body2'>{toRevoke?.reason}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToRevoke(undefined)}>{formatMessage(messages.cancel)}</Button>
          <Button color='error' variant='contained' onClick={onRevoke}>
            {formatMessage(messages.revoke)}
          </Button>
        </DialogActions>
      </Dialog>

      {/* A SIBLING of the revoke dialog (not nested) — a nested dialog unmounts with its parent's
          open={false} subtree and would never render, making Start unreachable. */}
      <Dialog open={Boolean(toStart)} onClose={() => setToStart(undefined)}>
        <DialogTitle>{formatMessage(messages.startConfirmTitle)}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {formatMessage(messages.startConfirmBody)}
          </DialogContentText>
          <Typography variant='body2' sx={{ color: '#636363' }}>
            {formatMessage(messages.targetColumn)}
          </Typography>
          <Typography variant='body2'>
            {nameOrId(
              toStart?.targetDisplayName,
              formatMessage(messages.userIdShort, { id: toStart?.targetUserId ?? 0 })
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToStart(undefined)}>{formatMessage(messages.cancel)}</Button>
          <Button color='primary' variant='contained' onClick={onStart}>
            {formatMessage(messages.startConfirm)}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Break-glass bulk-revoke (WI 619): confirm is gated on the operator typing the exact phrase. */}
      <Dialog open={bulkOpen} onClose={closeBulk} fullWidth maxWidth='sm'>
        <DialogTitle>{formatMessage(messages.bulkRevokeTitle)}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {formatMessage(messages.bulkRevokeBody, {
              phrase: <strong>{BULK_REVOKE_CONFIRM_PHRASE}</strong>,
            })}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size='small'
            label={formatMessage(messages.bulkRevokeInputLabel)}
            value={bulkPhrase}
            onChange={(e) => setBulkPhrase(e.target.value)}
            inputProps={{ 'aria-label': 'bulk-revoke-confirm-phrase' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBulk}>{formatMessage(messages.cancel)}</Button>
          <Button
            color='error'
            variant='contained'
            onClick={onBulkRevoke}
            disabled={bulkPhrase !== BULK_REVOKE_CONFIRM_PHRASE || isEndingAll}
          >
            {formatMessage(messages.bulkRevokeConfirm)}
          </Button>
        </DialogActions>
      </Dialog>

      <Stack direction='row' spacing={2} alignItems='flex-end' sx={{ mb: 2, flexWrap: 'wrap' }}>
        <TextField
          select
          size='small'
          label={formatMessage(messages.filterStatus)}
          value={status}
          onChange={(e) => setStatus(e.target.value as ImpersonationGrantStatus | '')}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value=''>{formatMessage(messages.filterStatusAll)}</MenuItem>
          {IMPERSONATION_GRANT_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size='small'
          type='number'
          label={formatMessage(messages.filterTenant)}
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          inputProps={{ min: 1 }}
        />
        <TextField
          size='small'
          type='number'
          label={formatMessage(messages.filterGrantee)}
          value={granteeId}
          onChange={(e) => setGranteeId(e.target.value)}
          inputProps={{ min: 1 }}
        />
        <Button variant='contained' onClick={applyFilters}>
          {formatMessage(messages.filterApply)}
        </Button>
        <Button onClick={clearFilters}>{formatMessage(messages.filterClear)}</Button>
      </Stack>

      {data?.hasMore && (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
          {formatMessage(messages.hasMoreNotice)}
        </Typography>
      )}

      <MRTDataGridv2
        state={{ isLoading: isFetching || !grantsLoaded }}
        initialState={{
          sorting: [{ id: 'startsAt', desc: true }],
          pagination: { pageIndex: 0, pageSize: 50 },
        }}
        leftHeadingComponent={
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <TableTitleTypography>{formatMessage(messages.title)}</TableTitleTypography>
            {eventCount != null && eventCount > 0 && (
              <Chip size='small' variant='outlined' label={formatMessage(messages.eventsBadge, { count: eventCount })} />
            )}
          </Box>
        }
        rightHeadingComponent={
          <Stack direction='row' spacing={1} alignItems='center'>
            <Button
              variant='outlined'
              size='small'
              disabled={grants.length === 0}
              onClick={() => downloadCsv('impersonation-grants.csv', buildGrantsCsv(grants))}
            >
              {formatMessage(messages.exportCsv)}
            </Button>
            <Button color='error' variant='outlined' size='small' onClick={() => setBulkOpen(true)}>
              {formatMessage(messages.bulkRevoke)}
            </Button>
            <AddButton text={formatMessage(messages.newGrant)} onClick={() => setNewOpen(true)} />
          </Stack>
        }
        columns={columns}
        data={grants}
        getRowId={(row) => String(row.grantId)}
        enableExpanding
        renderDetailPanel={({ row }: { row: { original: ImpersonationGrantSummary } }) => (
          <GrantSessionsPanel grantId={row.original.grantId} />
        )}
        enableRowActions
        positionActionsColumn='last'
        renderRowActions={({ row }: { row: { original: ImpersonationGrantSummary } }) =>
          row.original.status === 'Active' ? (
            <Stack direction='row' spacing={1}>
              {activePersonaId === row.original.granteeUserId && !alreadyImpersonating && (
                <Button
                  size='small'
                  color='primary'
                  variant='contained'
                  disabled={isStarting}
                  onClick={() => setToStart(row.original)}
                >
                  {formatMessage(messages.startAction)}
                </Button>
              )}
              <Button
                size='small'
                color='error'
                variant='outlined'
                onClick={() => setToRevoke(row.original)}
              >
                {formatMessage(messages.revoke)}
              </Button>
            </Stack>
          ) : null
        }
      />
    </>
  )
}

export default ImpersonationGrantsPage
